import React, { ReactNode, useState } from "react"
import { Autocomplete, Box, Progress, TextField, Theme, Typography } from "@carrotfertility/carotene-core"
import { LabelWithIconTooltip } from "./LabelWithIconTooltip"
// eslint-disable-next-line no-restricted-imports
import { SxProps } from "@mui/system"
import { useIntl } from "react-intl"
import { TypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import {
  renderAutocompleteProviderOption,
  useDebouncedSearch
} from "#/pages/provider-finder/components/keyword-search/keywordSearchUtils"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { trpc } from "#/lib/trpc/client"
import { trpcRetry } from "#/lib/trpc/retryPolicy"

const TYPEAHEAD_PROVIDERS_QUERY_KEY = "GetTypeaheadProviders"

// countryCode isn't passed — the BFF scopes to the member's company country
// from the session, so the FE doesn't have to source (or be trusted with) it.
function useGetTypeaheadProviders(
  providerNameSubstring: string,
  providerType?: string
): UseQueryResult<TypeaheadProvider[], ResponseError> {
  return useQuery<TypeaheadProvider[], ResponseError>(
    [TYPEAHEAD_PROVIDERS_QUERY_KEY, providerNameSubstring, providerType],
    async () => {
      const result = await trpc.providerFinder.typeahead.query({
        name: providerNameSubstring,
        providerType
      })
      return result.data.map((p) => ({ uuid: p.id, name: p.name, address: p.address, city: p.city }))
    },
    {
      enabled: !!providerNameSubstring && !!providerType,
      refetchOnWindowFocus: false,
      retry: trpcRetry(1)
    }
  )
}

export function KeywordAutocomplete({
  tooltipTitle,
  label,
  keyword,
  popupIcon,
  startAdornment,
  sx,
  setProviderNameValue,
  setProviderIdValue,
  setTypeaheadLocationSearch,
  providerType,
  placeholder
}: {
  tooltipTitle?: ReactNode
  label?: string
  keyword?: string
  popupIcon: ReactNode
  startAdornment: ReactNode
  sx?: SxProps<Theme>
  setProviderNameValue: (input: string) => void
  setProviderIdValue?: (input: string) => void
  setTypeaheadLocationSearch?: (input: string) => void
  providerType?: string
  placeholder?: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [userValue, setUserValue] = useState(null)

  const {
    inputValue,
    loading: isTyping,
    setInputValue,
    searchValue
  } = useDebouncedSearch(setProviderNameValue, setOpen)

  const { data: providerPredictions, isLoading: isProviderPredictionsLoading } = useGetTypeaheadProviders(
    searchValue,
    providerType
  )

  const isSearchLoading = isTyping || isProviderPredictionsLoading
  const { isDoulasSearched } = useProviderTypeSearched(providerType)

  const intl = useIntl()

  return (
    <>
      <Autocomplete
        disabled={!providerType}
        id="keyword-search-autocomplete"
        sx={sx}
        autoComplete
        clearOnEscape
        filterOptions={(x) => x}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        loading={isSearchLoading}
        loadingText={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            padding={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
            sx={{ grow: 1 }}
          >
            <Progress />
          </Box>
        }
        noOptionsText={<Typography>{intl.formatMessage({ defaultMessage: "No results found" })}</Typography>}
        options={isSearchLoading ? [] : providerPredictions}
        popupIcon={popupIcon}
        value={keyword || userValue || inputValue}
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        onChange={(event, newUserValue) => {
          setUserValue(newUserValue)
          if (newUserValue && typeof newUserValue !== "string") {
            setProviderNameValue(newUserValue.name)
            setProviderIdValue?.(String(newUserValue.uuid))
            if (newUserValue.address && setTypeaheadLocationSearch) {
              setTypeaheadLocationSearch(newUserValue.address)
            }
          } else {
            setProviderNameValue(newUserValue || "")
            setProviderIdValue?.("")
          }
          if (newUserValue) {
            setOpen(false)
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          // Typing after a pick means the member has diverged from the
          // specific selection — drop the id so the results fall back to
          // a name-based search instead of hydrating the stale pick.
          if (reason === "input") {
            setProviderIdValue?.("")
          }
          if (reason === "reset") {
            setInputValue(null)
          } else {
            setInputValue(newInputValue)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              label && (
                <LabelWithIconTooltip
                  {...{ label, required: true }}
                  tooltipTitle={isDoulasSearched ? null : tooltipTitle}
                />
              )
            }
            fullWidth
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: startAdornment
            }}
            defaultValue={keyword || null}
          />
        )}
        renderOption={(props, providerPrediction) =>
          renderAutocompleteProviderOption(props, providerPrediction, inputValue)
        }
      />
    </>
  )
}
